import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Archive, ArchiveRestore, Pencil, Trash2 } from 'lucide-react';

import {
  errorMessage,
  fieldIssues,
  isConflict,
  useCreateSystem,
  useDeleteSystem,
  usePatchSystem,
} from '../api/planning-hooks';
import type { CreateSystemBody, PatchSystemBody } from '../api/planning';
import type { RegistryDocument, SystemEntry } from '../api/types';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { RegistryGate } from '../components/RegistryGate';
import { PageHeader } from '../components/view-parts';
import { Badge, Button, EmptyState, Field, Input, Modal, Select, Textarea } from '../components/ui';
import { projectWord } from '../lib/plural';
import { useQueryParams } from '../lib/query-params';

/**
 * F-21 — the managed catalogue of systems (a read-only inherited list until
 * 2026-10-14): table view (STYLEGUIDE §5), client-side search, URL-synced
 * «Показать архивные» toggle, create/edit modal, confirm-free archive toggle
 * per row and a delete that the API refuses while projects link the system.
 *
 * NOTE: no <SummaryChips /> here on purpose — the summary pills count
 * PROJECTS, which has no meaning for the systems catalogue (owner, 2026-10-14).
 */
export function SystemsPage(): ReactElement {
  return <RegistryGate label="Загрузка каталога систем">{(registry) => <SystemsView registry={registry} />}</RegistryGate>;
}

function SystemsView({ registry }: { registry: RegistryDocument }): ReactElement {
  const { params, setParams } = useQueryParams();
  /** URL-synced (STYLEGUIDE §6): off = no key, so the default view has a canonical URL. */
  const showArchived = params.archived === '1';
  const [search, setSearch] = useState('');
  /** `undefined` = closed, `null` = creating, `SystemEntry` = editing. */
  const [editing, setEditing] = useState<SystemEntry | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<SystemEntry | null>(null);
  const remove = useDeleteSystem();
  const toggleArchive = usePatchSystem();

  const personById = useMemo(() => new Map(registry.people.map((person) => [person.id, person])), [registry.people]);
  const linkedCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const project of registry.projects) {
      for (const systemId of project.systemIds ?? []) {
        counts.set(systemId, (counts.get(systemId) ?? 0) + 1);
      }
    }
    return counts;
  }, [registry.projects]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return registry.systems.filter((system) => {
      if (!showArchived && system.archived) return false;
      if (query === '') return true;
      return [system.name, system.short, system.tags.join(' ')].join(' ').toLowerCase().includes(query);
    });
  }, [registry.systems, search, showArchived]);

  const filtersActive = search.trim() !== '' || showArchived;
  const resetFilters = () => {
    setSearch('');
    if (showArchived) setParams({ archived: null });
  };

  const archive = (system: SystemEntry) =>
    toggleArchive.mutate({
      id: system.id,
      body: { version: registry.version, archived: !system.archived },
    });

  return (
    <>
      <PageHeader
        title="Системы"
        sub="Управляемый каталог систем ИТ-департамента (F-21): создание, правка, архив, ответственный, ссылки на проекты."
      />

      <div className="toolbar">
        <div className="toolbar__field toolbar__field--grow">
          <label className="toolbar__label" htmlFor="systems-search">
            Поиск
          </label>
          <input
            id="systems-search"
            type="search"
            className="input input--compact"
            placeholder=""
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <label className="check-field" htmlFor="systems-archived">
          <input
            id="systems-archived"
            type="checkbox"
            checked={showArchived}
            onChange={(event) => setParams({ archived: event.target.checked ? '1' : null })}
          />
          Показать архивные
        </label>
        <Button variant="primary" size="sm" onClick={() => setEditing(null)}>
          Новая система
        </Button>
      </div>

      <section className="card card--flush">
        {registry.systems.length === 0 ? (
          <EmptyState title="Каталог систем пуст" hint="Добавьте первую систему кнопкой «Новая система»." />
        ) : visible.length === 0 ? (
          <EmptyState
            title="Ничего не найдено"
            hint="Фильтры или поиск исключили все системы."
            action={
              filtersActive ? (
                <Button size="sm" variant="secondary" onClick={resetFilters}>
                  Сбросить фильтры
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <caption className="sr-only">Каталог систем</caption>
              <thead>
                <tr>
                  <th scope="col">Название</th>
                  <th scope="col">URL</th>
                  <th scope="col">GitHub</th>
                  <th scope="col">Ответственный</th>
                  <th scope="col">Теги</th>
                  <th scope="col" className="num">
                    Проектов
                  </th>
                  <th scope="col">Архив</th>
                  <th scope="col" className="num" aria-label="Действия" />
                </tr>
              </thead>
              <tbody>
                {visible.map((system) => {
                  const owner = system.ownerPersonId !== null ? personById.get(system.ownerPersonId) : undefined;
                  const count = linkedCount.get(system.id) ?? 0;
                  return (
                    <tr key={system.id} className={system.archived ? 'system-row--archived' : undefined}>
                      <td className="cell-primary">
                        <span className="cell-primary__text" title={system.name}>
                          {system.name}
                        </span>
                        {system.short ? <span className="cell-sub">{system.short}</span> : null}
                      </td>
                      <td className="cell-muted">
                        <CellLink url={system.url} />
                      </td>
                      <td className="cell-muted">
                        <CellLink url={system.repoUrl} withPath />
                      </td>
                      <td className="cell-muted">{owner ? owner.name : '—'}</td>
                      <td>
                        {system.tags.length === 0 ? (
                          <span className="cell-muted">—</span>
                        ) : (
                          <div className="badge-row">
                            {system.tags.map((tag) => (
                              <Badge key={tag}>{tag}</Badge>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="num" title={count > 0 ? `Связана с ${count} ${projectWord(count)}` : undefined}>
                        {count}
                      </td>
                      <td>{system.archived ? <Badge tone="warn">в архиве</Badge> : null}</td>
                      <td className="num">
                        <div className="row-actions">
                          <button
                            type="button"
                            className="icon-btn icon-btn--sm"
                            aria-label={`Редактировать: ${system.name}`}
                            title="Редактировать"
                            onClick={() => setEditing(system)}
                          >
                            <Pencil size={14} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="icon-btn icon-btn--sm"
                            aria-label={system.archived ? `Вернуть из архива: ${system.name}` : `В архив: ${system.name}`}
                            title={system.archived ? 'Вернуть из архива' : 'В архив'}
                            disabled={toggleArchive.isPending}
                            onClick={() => archive(system)}
                          >
                            {system.archived ? (
                              <ArchiveRestore size={14} aria-hidden="true" />
                            ) : (
                              <Archive size={14} aria-hidden="true" />
                            )}
                          </button>
                          <button
                            type="button"
                            className="icon-btn icon-btn--sm icon-btn--danger"
                            aria-label={`Удалить: ${system.name}`}
                            title="Удалить"
                            onClick={() => setDeleting(system)}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing !== undefined ? (
        <SystemModal open onClose={() => setEditing(undefined)} registry={registry} system={editing} />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title="Удалить систему?"
        busy={remove.isPending}
        error={errorMessage(remove.error)}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          remove.mutate({ id: deleting.id, version: registry.version }, { onSuccess: () => setDeleting(null) });
        }}
        description={
          <p>
            {deleting ? `«${deleting.name}» будет удалена из каталога. ` : ''}
            Каскад: пока на систему ссылается {deleting ? (linkedCount.get(deleting.id) ?? 0) : 0}{' '}
            {projectWord(deleting ? (linkedCount.get(deleting.id) ?? 0) : 0)}, удаление будет отклонено (422) —
            сначала снимите связи в карточках проектов. Вместо удаления систему можно отправить в архив.
          </p>
        }
      />
    </>
  );
}

/**
 * A link rendered compactly so a long URL cannot push the row out (STYLEGUIDE
 * §5): the visible text is the hostname — or `host + path` for GitHub repos —
 * clipped with ellipsis, the full URL lives in `title` and `href`.
 */
function CellLink({ url, withPath = false }: { url: string; withPath?: boolean }): ReactElement {
  if (url.trim() === '') return <span>—</span>;
  let label = url;
  try {
    const parsed = new URL(url);
    label = withPath ? `${parsed.host}${parsed.pathname.replace(/\/+$/, '')}` : parsed.host;
  } catch {
    // Not a parsable URL — show it as-is, still a link (`title` carries it all).
  }
  return (
    <a className="cell-link" href={url} target="_blank" rel="noreferrer" title={url}>
      {label}
    </a>
  );
}

interface SystemModalProps {
  open: boolean;
  onClose: () => void;
  registry: RegistryDocument;
  /** null = create a new system. */
  system: SystemEntry | null;
}

/** Comma-separated input → `string[]` (empty/blank items dropped). */
function splitTags(raw: string): string[] {
  return raw
    .split(',')
    .map((tag) => tag.trim())
    .filter((tag) => tag !== '');
}

function SystemModal({ open, onClose, registry, system }: SystemModalProps): ReactElement {
  const create = useCreateSystem();
  const patch = usePatchSystem();
  const [name, setName] = useState(system?.name ?? '');
  const [short, setShort] = useState(system?.short ?? '');
  const [description, setDescription] = useState(system?.description ?? '');
  const [tags, setTags] = useState(system ? system.tags.join(', ') : '');
  const [url, setUrl] = useState(system?.url ?? '');
  const [repoUrl, setRepoUrl] = useState(system?.repoUrl ?? '');
  const [ownerPersonId, setOwnerPersonId] = useState(system?.ownerPersonId ?? '');
  const [archived, setArchived] = useState(system?.archived ?? false);
  const [error, setError] = useState<string | null>(null);

  const busy = create.isPending || patch.isPending;
  const mutationError = (create.error ?? patch.error) as Error | null;
  const conflict = isConflict(mutationError);
  const issueOf = (field: string): string | undefined => fieldIssues(mutationError).get(field);

  const save = () => {
    if (name.trim() === '') {
      setError('Название обязательно');
      return;
    }
    // Strings always travel (sending '' is how a field is CLEARED — arch §3.2);
    // `ownerPersonId: null` clears the owner; tags is the whole list.
    const fields = {
      name: name.trim(),
      short: short.trim(),
      description: description.trim(),
      tags: splitTags(tags),
      url: url.trim(),
      repoUrl: repoUrl.trim(),
      ownerPersonId: ownerPersonId || null,
      archived,
    };
    if (system) {
      const set: Partial<Omit<PatchSystemBody, 'version'>> = {};
      for (const [key, value] of Object.entries(fields)) {
        const current = (system as unknown as Record<string, unknown>)[key];
        const changed = Array.isArray(value) ? JSON.stringify(current) !== JSON.stringify(value) : current !== value;
        if (changed) (set as Record<string, unknown>)[key] = value;
      }
      if (Object.keys(set).length === 0) return onClose();
      // Dictionary patches are FLAT: {version, …fields} — no `set` wrapper.
      patch.mutate({ id: system.id, body: { version: registry.version, ...set } }, { onSuccess: onClose });
    } else {
      create.mutate({ version: registry.version, ...fields } satisfies CreateSystemBody, { onSuccess: onClose });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={system ? 'Редактировать систему' : 'Новая система'}
      description="id стабилен: переименование системы не ломает ссылки проектов (F-21)."
      footer={
        <>
          {conflict ? (
            <span className="badge badge--warn" title={mutationError?.message}>
              Изменена кем-то другим — данные обновлены
            </span>
          ) : mutationError ? (
            <span className="badge badge--bad" role="alert" title={mutationError.message}>
              Не сохранено
            </span>
          ) : null}
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button variant="primary" size="sm" onClick={save} loading={busy}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <div className="form-grid__full">
          <Field label="Название" htmlFor="system-name" required error={error ?? issueOf('name')}>
            <Input
              id="system-name"
              value={name}
              aria-invalid={error !== null || issueOf('name') !== undefined ? true : undefined}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
        </div>
        <Field label="Короткое описание" htmlFor="system-short">
          <Input id="system-short" value={short} onChange={(event) => setShort(event.target.value)} />
        </Field>
        <Field label="Ответственный" htmlFor="system-owner" hint="Справочник людей; «—» — ответственного нет" error={issueOf('ownerPersonId')}>
          <Select id="system-owner" value={ownerPersonId} onChange={(event) => setOwnerPersonId(event.target.value)}>
            <option value="">—</option>
            {registry.people.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="URL" htmlFor="system-url" hint="Адрес системы; пусто — ссылки нет" error={issueOf('url')}>
          <Input id="system-url" type="url" value={url} onChange={(event) => setUrl(event.target.value)} />
        </Field>
        <Field label="GitHub" htmlFor="system-repo" hint="Ссылка на репозиторий" error={issueOf('repoUrl')}>
          <Input id="system-repo" type="url" value={repoUrl} onChange={(event) => setRepoUrl(event.target.value)} />
        </Field>
        <div className="form-grid__full">
          <Field label="Теги" htmlFor="system-tags" hint="Через запятую" error={issueOf('tags')}>
            <Input id="system-tags" value={tags} onChange={(event) => setTags(event.target.value)} />
          </Field>
        </div>
        <div className="form-grid__full">
          <Field label="Описание" htmlFor="system-description">
            <Textarea
              id="system-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
        </div>
        <div className="form-grid__full">
          <label className="check-field" htmlFor="system-archived">
            <input
              id="system-archived"
              type="checkbox"
              checked={archived}
              onChange={(event) => setArchived(event.target.checked)}
            />
            В архиве
          </label>
        </div>
      </div>
    </Modal>
  );
}
